/** tm/legal/CompanyLegalSection.tsx — 업체 상세: 이 업체에 연결한 법령·판례·해석 (GET /companies/:id/items) + 내 메모·첨부(다운로드만) */
import { useEffect, useState } from 'react';
import { C, T, R } from '../components/ui';
import { getCompanyItems } from './legalApi';
import { shortSource, shortDate, MUTED } from './legalTheme';
import { SourceBadge, ItemTitle, itemTitleText } from './legalUi';
import { MemoFileList } from './LegalMemoFiles';
import type { CompanyLegalItem } from './legalTypes';

export default function CompanyLegalSection({ companyId, onError }: { companyId: string; onError?: (m: string) => void }) {
  const [rows, setRows] = useState<CompanyLegalItem[] | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    setRows(null); setErr(false);
    getCompanyItems(companyId).then(r => { if (alive) setRows(r); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [companyId]);
  if (err || (rows && rows.length === 0)) return null; // 연결이 없으면 자리를 차지하지 않는다

  return (
    <div style={{ marginTop: 16, border: `1px solid ${C.border}`, borderRadius: R.md, overflow: 'hidden', background: C.white }}>
      <div style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}`, fontSize: T.sm, fontWeight: 700, color: C.text, display: 'flex', gap: 6, alignItems: 'center' }}>
        📚 연결한 법령·판례·해석 {rows && <span style={{ color: MUTED, fontWeight: 500 }}>{rows.length}</span>}
      </div>
      {!rows && <div style={{ padding: 12, fontSize: T.sm, color: MUTED }}>불러오는 중…</div>}
      {rows?.map(r => (
        <div key={r.id} style={{ padding: '8px 12px', borderBottom: `1px solid ${C.bg}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <SourceBadge group={r.group} label={shortSource(r.source, r.source_label)} />
            <span style={{ fontSize: T.sm, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{[r.case_no, shortDate(r.decided_at)].filter(Boolean).join(' · ')}</span>
          </div>
          <div title={itemTitleText(r.title, r.custom_title)} style={{ fontSize: T.sm, color: C.text, lineHeight: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><ItemTitle title={r.title} custom={r.custom_title} /></div>
          {r.link_note && <div style={{ fontSize: T.sm, color: C.sub, marginTop: 2 }}>메모: {r.link_note}</div>}
          {r.memo && r.memo !== r.link_note && <div style={{ fontSize: T.sm, color: C.sub, marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>내 메모: {r.memo}</div>}
          {r.files && r.files.length > 0 && <MemoFileList itemId={r.id} files={r.files} onToast={onError ? (m, k) => { if (k === 'error') onError(m); } : undefined} />}
        </div>
      ))}
    </div>
  );
}
